import { axiosClient } from '@/libs/axios-client';

/**
 * Uploads multiple files to the server (S3) using multipart/form-data.
 * @param {FormData} formData - The FormData object containing files and additional metadata.
 * @returns {Promise<Object>} A promise that resolves with the server response containing upload results.
 * @throws Will throw an error if the upload request fails.
 */
export const uploadMultipleFiles = async (formData) => {
  try {
    const response = await axiosClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload multiple files:', error);
    throw error;
  }
};

/**
 * Deletes a file from the server (S3) using its S3 key.
 * @param {Object} payload - Object containing the key of the file to delete.
 * @param {string} payload.key - The S3 key (path) of the file to delete.
 * @returns {Promise<Object>} A promise that resolves with the server response confirming deletion.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteFile = async ({ key }) => {
  try {
    const response = await axiosClient.delete('/files/delete', {
      params: { key },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to delete file:', error);
    throw error;
  }
};

/**
 * Retrieves a presigned URL for viewing or downloading a file from S3.
 * @param {Object} payload - Object containing the key of the file.
 * @param {string} payload.key - The S3 object key (relative path inside the bucket).
 * @returns {Promise<Object>} A promise that resolves with an object containing the presigned URL.
 * @throws Will throw an error if the request fails.
 */
export const downloadFile = async ({ key }) => {
  try {
    const response = await axiosClient.get('/files/download', {
      params: { key },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate presigned download URL:', error);
    throw error;
  }
};

/**
 * Downloads a file via server-proxied endpoint without opening it in the browser.
 * Calls GET /files/download-direct?key=... and forces a save-as using an object URL.
 *
 * @param {Object} params
 * @param {string} params.key           - S3 object key (path in bucket)
 * @param {string} [params.fallbackName] - Optional fallback filename
 */
export const downloadFileDirect = async ({ key, fallbackName = 'download' }) => {
  if (!key) throw new Error('File key is required.');

  const parseFilename = (cdHeader, defaultName) => {
    if (!cdHeader) return defaultName;
    const star = /filename\*\s*=\s*([^']+''|)([^;]+)$/i.exec(cdHeader);
    if (star && star[2]) {
      try { return decodeURIComponent(star[2].replace(/(^")|("$)/g, '')); }
      catch { return star[2].replace(/(^")|("$)/g, ''); }
    }

    const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(cdHeader);
    if (plain && plain[2]) return plain[2];
    return defaultName;
  };

  const res = await axiosClient.get('/files/download-direct', {
    params: { key },
    responseType: 'blob',
  });

  const cd = res.headers['content-disposition'];
  const fallback =
    fallbackName ||
    String(key).split('/').pop() ||
    'download';

  const filename = parseFilename(cd, fallback);

  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 1000);
};

/**
 * The same file `downloadFileDirect` saves, handed back as a Blob instead —
 * for a caller that needs the bytes (to print them) rather than a download.
 *
 * @param {string} key - Storage key.
 * @returns {Promise<Blob>}
 */
export const retrieveFileDirectBlob = async (key) => {
  if (!key) throw new Error('File key is required.');

  const res = await axiosClient.get('/files/download-direct', {
    params: { key },
    responseType: 'blob',
  });

  return new Blob([res.data], {
    type: res.headers['content-type'] || 'application/pdf',
  });
};

/**
 * Uploads a base64-encoded signature image to S3.
 * @param {Object} payload - Object containing signature data and company ID.
 * @param {string} payload.signatureData - Base64-encoded image data (with or without data URI prefix).
 * @param {string} payload.companyId - Company ID for folder organization.
 * @returns {Promise<Object>} A promise that resolves with the server response containing upload metadata.
 * @throws Will throw an error if the upload request fails.
 */
export const uploadSignature = async ({ signatureData, companyId }) => {
  try {
    const response = await axiosClient.post('/files/upload-signature', {
      signatureData,
      companyId,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload signature:', error);
    throw error;
  }
};

/**
 * Uploads a single file to S3 without creating DB records.
 * Used for memorandum, guideline, and similar standalone file uploads.
 * @param {File} file - The file to upload.
 * @returns {Promise<Object>} A promise that resolves with { key, location, mimetype, originalName }.
 */
export const uploadSingleFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosClient.post('/files/upload-guideline', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload file:', error);
    throw error;
  }
};

const FilesService = {
  retrieveFileDirectBlob,
  uploadMultipleFiles,
  deleteFile,
  downloadFile,
  downloadFileDirect,
  uploadSignature,
  uploadSingleFile,
};

export default FilesService;
